import { DateTime } from 'luxon';

/**
 * Type guard that checks if a value is a valid DateTime object or can be converted to one.
 *
 * @param value - The value to check
 * @param convert - If true, attempts to convert string or Date values to DateTime objects
 * @returns A type predicate indicating whether the value is a valid DateTime object
 *
 * @example
 * // Check if value is already a DateTime
 * isDateTime(dateTimeValue);
 *
 * @example
 * // Check if value is or can be converted to a valid DateTime
 * isDateTime(stringOrDateValue, true);
 */
export function isDateTime(value: unknown, convert = false): value is DateTime {
  if (!value) return false;
  if (value instanceof DateTime) return value.isValid;
  if (!convert) return false;

  if (typeof value === 'string') return DateTime.fromISO(value).isValid;
  if (value instanceof Date) return DateTime.fromJSDate(value).isValid;

  return false;
}

/**
 * Gets a DateTime when the value is already valid DateTime object or can be converted to one.
 *
 * @param value - The value to check
 * @param convert - If true, attempts to convert string or Date values to DateTime objects
 * @returns A DateTime object if the value is valid or can be converted, otherwise undefined
 *
 * @example
 * // Get value when it's already a DateTime
 * toDateTime(dateTimeValue);
 *
 * @example
 * // Get value when it can be converted to a valid DateTime
 * toDateTime(stringOrDateValue, true);
 */
export function toDateTime(value: unknown, convert = false): DateTime | undefined {
  if (!value) return undefined;
  if (value instanceof DateTime) return value.isValid ? value : undefined;
  if (!convert) return undefined;

  if (typeof value === 'string') {
    const iso = DateTime.fromISO(value);
    return iso.isValid ? iso : undefined;
  }
  if (value instanceof Date) {
    const js = DateTime.fromJSDate(value);
    return js.isValid ? js : undefined;
  }

  return undefined;
}

/** Converts a value of unknown type to a DateTime. An alias for toDateTime() with convert=true. */
export const convertDateTime = (value: unknown): DateTime | undefined => toDateTime(value, true);

/**
 * Parses a string into a DateTime, with optional normalization.
 *
 * @param value - The string to parse into a DateTime
 * @param normalize - Optional function that transforms the value before parsing
 * @returns A DateTime object if parsing is successful, otherwise undefined
 */
export function parseDateTime(value?: string, normalize?: (value: unknown) => string): DateTime | undefined {
  const normalized = normalize ? normalize(value) : value;
  return toDateTime(normalized, true);
}

// -- Formatting helpers --
// These use Luxon's locale-aware presets (backed by Intl.DateTimeFormat).
// In en-US: DATE_MED → "Apr 4, 2025", DATETIME_MED → "Apr 4, 2025, 2:30 PM"

/** Format an ISO string for display: "Apr 4, 2025" (locale-aware) */
export function formatDate(iso: string): string {
  return DateTime.fromISO(iso).toLocaleString(DateTime.DATE_MED);
}

/** Format an ISO string as date+time: "Apr 4, 2025, 2:30 PM" (locale-aware) */
export function formatDateTime(iso: string): string {
  return DateTime.fromISO(iso).toLocaleString(DateTime.DATETIME_MED);
}

/** Create a DateTime from a Unix timestamp in seconds (e.g. Stripe API responses). */
export function fromUnixSeconds(seconds: number): DateTime {
  return DateTime.fromSeconds(seconds);
}

/**
 * Format an ISO string as a short relative age: "just now", "20 minutes ago",
 * "3 hours ago". Returns an empty string if the input is invalid or more than
 * 24 hours old (callers should fall back to `formatDate` past that).
 *
 * Locale-aware via Luxon's `toRelative`. Computed against `now` at call
 * time — callers that want a reactive label must re-invoke on a ticker.
 */
export function formatRelativeAge(iso: string, now: DateTime = DateTime.now()): string {
  const dt = DateTime.fromISO(iso);
  if (!dt.isValid) return '';
  const diffHours = now.diff(dt, 'hours').hours;
  if (diffHours < 0 || diffHours >= 24) return '';
  if (diffHours < 1 / 60) return 'just now';
  return dt.toRelative({ base: now, style: 'long' }) ?? '';
}

// Intentionally did not implement asDateTime() function. If you want coercion just use the 'as' keyword.
